import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Grupos";
const MODULO_ACTAS = "Actas";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListarGrupos = ({ refresh }) => {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS POR MÓDULO
  // ==============================
  const getModuloPorNombre = (nombreModulo) => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(nombreModulo);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloGrupos = useMemo(() => getModuloPorNombre(MODULO), []);
  const moduloActas = useMemo(() => getModuloPorNombre(MODULO_ACTAS), []);

  const puedeCrear =
    moduloGrupos?.puedeCrear === true ||
    moduloGrupos?.crear === true ||
    moduloGrupos?.create === true;

  const puedeActualizar =
    moduloGrupos?.puedeActualizar === true ||
    moduloGrupos?.actualizar === true ||
    moduloGrupos?.editar === true ||
    moduloGrupos?.update === true;

  const puedeEliminar =
    moduloGrupos?.puedeEliminar === true ||
    moduloGrupos?.eliminar === true ||
    moduloGrupos?.delete === true;

  const puedeLeerActas =
    moduloActas?.puedeLeer === true ||
    moduloActas?.leer === true ||
    moduloActas?.read === true ||
    moduloActas?.ver === true ||
    moduloActas?.puedeVer === true;

  // ==============================
  // VALIDAR SESIÓN
  // ==============================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login");
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // ==============================
  // ELIMINAR GRUPO
  // ==============================
  const deleteGrupo = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar grupos.",
          "warning"
        );
        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        setDeletingId(id);

        const { data } = await axios.delete(`/api/grupos/${id}`, authConfig());

        setGrupos((prev) => prev.filter((grupo) => grupo._id !== id));

        Swal.fire({
          icon: "success",
          title: data?.message || "Grupo eliminado",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar el grupo:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar el grupo.",
          "error"
        );
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  // ==============================
  // CARGAR DATOS
  // ==============================
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/grupos/listall/", authConfig());

      const lista = Array.isArray(data?.grupos) ? data.grupos : [];

      setGrupos([...lista].reverse());
    } catch (error) {
      console.error("Error al cargar los grupos:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "No se pudo cargar la lista de grupos.",
        "error"
      );

      setGrupos([]);
    } finally {
      setLoading(false);
    }
  }, [manejarErrorSesion]);

  useEffect(() => {
    getData();
  }, [getData, refresh]);

  // ==============================
  // DATA TABLE
  // ==============================
  const dataTabla = useMemo(
    () =>
      grupos.map((grupo) => ({
        ...grupo,

        nombrePrograma: (
          <div title={grupo.programaObjectId?.nombrePrograma || ""}>
            {grupo.programaObjectId?.nombrePrograma ||
              "Programa no disponible"}
          </div>
        ),

        nombreCentro: (
          <div title={grupo.centroObjectId?.nombreCentro || ""}>
            {grupo.centroObjectId?.nombreCentro || "Centro no disponible"}
          </div>
        ),

        nombreCiudad: (
          <div title={grupo.ciudadObjectId?.nombreCiudad || ""}>
            {grupo.ciudadObjectId?.nombreCiudad || "Ciudad no disponible"}
          </div>
        ),

        finiciolectivaGrupo: grupo.finiciolectivaGrupo
          ? new Date(grupo.finiciolectivaGrupo).toLocaleDateString("es-CO")
          : "",

        ffinlectivaGrupo: grupo.ffinlectivaGrupo
          ? new Date(grupo.ffinlectivaGrupo).toLocaleDateString("es-CO")
          : "",

        funciones: (
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            {puedeLeerActas && (
              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/institucional/actas/${grupo.fichaGrupo}`)
                }
                title="Listar Actas del Grupo"
                type="button"
                disabled={loading || deletingId === grupo._id}
              >
                <i className="fa-solid fa-list-check"></i>
              </button>
            )}

            {puedeActualizar && (
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                disabled={loading || deletingId === grupo._id}
                onClick={() =>
                  navigate(`/institucional/grupos/edit/${grupo._id}`)
                }
                title="Editar Grupo"
                type="button"
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>
            )}

            {puedeEliminar && (
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                disabled={loading || deletingId === grupo._id}
                onClick={() => deleteGrupo(grupo._id)}
                title="Eliminar Grupo"
                type="button"
              >
                {deletingId === grupo._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            )}
          </div>
        ),
      })),
    [
      grupos,
      loading,
      deletingId,
      puedeLeerActas,
      puedeActualizar,
      puedeEliminar,
      navigate,
      deleteGrupo,
    ]
  );

  const columns = useMemo(
    () => [
      { Header: "Ficha", accessor: "fichaGrupo" },
      { Header: "Programa", accessor: "nombrePrograma" },
      { Header: "Centro", accessor: "nombreCentro", width: 300 },
      { Header: "F.I Lectiva", accessor: "finiciolectivaGrupo" },
      { Header: "F.F Lectiva", accessor: "ffinlectivaGrupo" },
      { Header: "Ciudad", accessor: "nombreCiudad", width: 240 },
      { Header: "Estado", accessor: "estadoGrupo" },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  // ==============================
  // RENDER
  // ==============================
  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Grupos de Formación
                </h5>

                <small className="text-secondary">
                  Gestión institucional de grupos y actas.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">

                 <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() => navigate("/inicio")}
                  title="Volver al inicio"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={getData}
                  disabled={loading}
                  title="Actualizar"
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Actualizar
                </button>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    disabled={loading}
                    onClick={() => navigate("/institucional/grupos/nuevo")}
                    title="Crear Grupo"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status"></div>
                <div className="mt-3">Cargando grupos…</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={dataTabla} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">👥</div>

                <div className="fw-semibold">No hay grupos registrados</div>

                <div className="small mb-3">
                  Crea el primer grupo para iniciar la gestión.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate("/institucional/grupos/nuevo")}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Crear grupo
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarGrupos;